import type { FieldDefinition } from '@/types/cgt'

/**
 * Write-once fields (`canChange: false`).
 *
 * A field flagged `canChange: false` is editable while it is empty and becomes
 * permanently read-only the moment it holds a value — one-shot entry, no
 * corrections. The rule lives here rather than inline in the renderer because
 * the renderer is `.tsx` and `vitest.config.ts` only collects `src/**\/*.test.ts`,
 * so anything expressed in a component is untestable in this repo.
 *
 * cgt-backend re-implements the same predicate in
 * `form-submissions.service.ts` (`saveFieldValue`). A disabled input is a
 * courtesy; the API is what actually refuses the write. Keep the two in step.
 */

/**
 * Whether a submission value counts as "the field has been filled in".
 *
 * `null`, `undefined` and `''` are unset, which is uncontroversial. `false` is
 * the interesting one: an untouched checkbox and a deliberately-unchecked
 * checkbox are the same value, so treating `false` as populated would freeze
 * every write-once checkbox the instant the form loaded, before anyone had
 * touched it. It is therefore NOT populated — a write-once checkbox locks when
 * it is ticked, and unticking it again is allowed.
 */
export function isFieldValuePopulated(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (value === '') return false
  if (value === false) return false
  if (Array.isArray(value)) return value.length > 0
  return true
}

/** Field types the write-once toggle is never offered on, and never applies to. */
const EXEMPT_FIELD_TYPES = new Set([
  // Recomputed server-side on every save (`recomputeCalculated`), so a lock
  // would refuse writes the user never made.
  'calculated',
  // Already has its own overwrite rule (a reason is required to re-sign).
  'signature',
  // Anchors a Process step; its state is driven by step execution/transitions,
  // not by a value the operator types into this field.
  'process',
  // The picture is a FormAttachment, not an entry in `submission.values`, so
  // the lock would read the field as permanently empty and never fire —
  // offering the toggle would promise a rule that cannot happen.
  'image',
  // Same for the multi-file Attachment field (z8nrz7dp7p).
  'attachment',
])

/**
 * Whether the write-once toggle should be offered for this field at all.
 * MR fields are excluded for the same reason as `process`: they are already
 * read-only and their value never enters `submission.values`.
 */
export function supportsWriteOnce(
  field: Pick<FieldDefinition, 'fieldType' | 'isMrField'>,
): boolean {
  if (field.isMrField) return false
  return !EXEMPT_FIELD_TYPES.has(field.fieldType)
}

/**
 * Whether this field is locked *because it is write-once and already filled*.
 *
 * Deliberately narrow: it answers only for the `canChange` rule. Form
 * completion, signature locks, prerequisite gates and section gates are
 * separate terms the renderer ORs alongside this one.
 */
export function isFieldWriteLocked(
  field: Pick<FieldDefinition, 'fieldType' | 'isMrField' | 'canChange'>,
  value: unknown,
): boolean {
  // Absent means editable — every definition saved before this property
  // existed has no value here, and none of them are write-once.
  if (field.canChange !== false) return false
  if (!supportsWriteOnce(field)) return false
  return isFieldValuePopulated(value)
}
